import type { ChangeEvent } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type {
  ISelectionConfig,
  ITablePagination,
  ITableSelection,
} from "../types";

type Props<Item> = {
  items: Item[];
  itemsOnCurrentPage: Item[];
  selectable?: false | ITableSelection<Item>;
  pagination?: ITablePagination;
};

export function useSelectionController<Item>({
  items,
  itemsOnCurrentPage,
  selectable,
  pagination,
}: Props<Item>): ISelectionConfig<Item> | undefined {
  const [selectedItems, setSelectedItems] = useState<Item[]>([]);
  const [selectAll, setSelectAll] = useState(false);

  const enabled = Boolean(selectable);

  const idKey =
    typeof selectable !== "boolean" ? (selectable?.idKey as keyof Item) : null;
  const onSelectAll =
    typeof selectable !== "boolean" ? selectable?.onSelectAll : undefined;
  const onSelectionChange =
    typeof selectable !== "boolean" ? selectable?.onSelectionChange : undefined;

  const hasMultiplePages = useMemo(() => {
    if (!pagination) return false;
    const totalPages = Math.ceil(pagination.totalItems / pagination.pageSize);
    return totalPages > 1;
  }, [pagination]);

  const showSelectAllButton = !!onSelectAll && hasMultiplePages;

  const getItemId = useCallback(
    (item: Item) => {
      if (!idKey) {
        throw new Error("getItemId called while disabled");
      }
      return item[idKey];
    },
    [idKey],
  );

  const isItemSelected = useCallback(
    (item: Item): boolean => {
      if (!enabled) {
        return false;
      }
      if (selectAll) {
        return true;
      }
      const itemId = getItemId(item);
      return selectedItems.some((s) => getItemId(s) === itemId);
    },
    [enabled, selectAll, selectedItems, getItemId],
  );

  const anchorIndexRef = useRef<number | null>(null);

  const indexOfOnPage = useCallback(
    (item: Item) => {
      const id = getItemId(item);
      return itemsOnCurrentPage.findIndex((it) => getItemId(it) === id);
    },
    [itemsOnCurrentPage, getItemId],
  );

  const applyRange = useCallback(
    (startIdx: number, endIdx: number, checked: boolean) => {
      const [lo, hi] =
        startIdx < endIdx ? [startIdx, endIdx] : [endIdx, startIdx];
      const rangeItems = itemsOnCurrentPage.slice(lo, hi + 1);

      let next = selectedItems;

      if (checked) {
        const existing = new Set(selectedItems.map(getItemId));
        const toAdd = rangeItems.filter((i) => !existing.has(getItemId(i)));
        next = [...selectedItems, ...toAdd];
      } else {
        const idsToRemove = new Set(rangeItems.map(getItemId));
        next = selectedItems.filter((i) => !idsToRemove.has(getItemId(i)));
      }

      setSelectedItems(next);
      onSelectionChange?.(next);
    },
    [itemsOnCurrentPage, selectedItems, getItemId, onSelectionChange],
  );

  const onSelectRow = useCallback(
    (item: Item) => {
      return (checked: boolean, e?: ChangeEvent<HTMLInputElement>) => {
        if (!enabled) {
          return;
        }

        if (
          (e?.nativeEvent as MouseEvent)?.shiftKey &&
          anchorIndexRef.current !== null
        ) {
          const currentIdx = indexOfOnPage(item);
          if (currentIdx !== -1) {
            applyRange(anchorIndexRef.current, currentIdx, checked);
            anchorIndexRef.current = currentIdx;
            return;
          }
        }

        const itemId = getItemId(item);

        if (selectAll) {
          if (!checked) {
            setSelectAll(false);
            const newSelected = itemsOnCurrentPage.filter(
              (p) => getItemId(p) !== itemId,
            );
            setSelectedItems(newSelected);
            onSelectionChange?.(newSelected);
          }
          anchorIndexRef.current = indexOfOnPage(item);
          return;
        }

        const newSelected = checked
          ? [...selectedItems, item]
          : selectedItems.filter((s) => getItemId(s) !== itemId);

        setSelectedItems(newSelected);
        onSelectionChange?.(newSelected);

        anchorIndexRef.current = indexOfOnPage(item);
      };
    },
    [
      enabled,
      selectAll,
      selectedItems,
      itemsOnCurrentPage,
      getItemId,
      onSelectionChange,
      indexOfOnPage,
      applyRange,
    ],
  );

  const onSelectAllFromPage = useCallback(
    (checked: boolean) => {
      if (!enabled) return;

      if (selectAll) {
        if (!checked) {
          setSelectAll(false);
          setSelectedItems([]);
          onSelectionChange?.([]);
        }
        return;
      }

      if (!checked) {
        const pageIdSet = new Set(itemsOnCurrentPage.map(getItemId));
        const newSelected = selectedItems.filter(
          (i) => !pageIdSet.has(getItemId(i)),
        );
        setSelectedItems(newSelected);
        onSelectionChange?.(newSelected);
      } else {
        const toAdd = itemsOnCurrentPage.filter((i) => !isItemSelected(i));
        const newSelected = [...selectedItems, ...toAdd];
        setSelectedItems(newSelected);
        onSelectionChange?.(newSelected);
      }
    },
    [
      enabled,
      selectAll,
      itemsOnCurrentPage,
      selectedItems,
      isItemSelected,
      getItemId,
      onSelectionChange,
    ],
  );

  const handleSelectAll = useCallback(() => {
    if (!enabled) {
      return;
    }
    setSelectAll(true);
    setSelectedItems([]);
    onSelectAll?.();
  }, [enabled, onSelectAll]);

  const allPageItemsSelected = useMemo(() => {
    return (
      enabled &&
      itemsOnCurrentPage.length > 0 &&
      itemsOnCurrentPage.every((i) => isItemSelected(i))
    );
  }, [enabled, itemsOnCurrentPage, isItemSelected]);

  const somePageItemsSelected = useMemo(() => {
    return (
      enabled &&
      itemsOnCurrentPage.some((i) => isItemSelected(i)) &&
      !allPageItemsSelected
    );
  }, [enabled, itemsOnCurrentPage, isItemSelected, allPageItemsSelected]);

  useEffect(() => {
    if (!enabled) {
      return;
    }
    setSelectedItems([]);
    setSelectAll(false);
    onSelectionChange?.([]);
  }, [enabled, items, itemsOnCurrentPage, onSelectionChange]);

  useEffect(() => {
    anchorIndexRef.current = null;
  }, [itemsOnCurrentPage]);

  if (!enabled) {
    return undefined;
  }

  return {
    selectedItems,
    selectAll,
    allPageItemsSelected,
    somePageItemsSelected,
    isItemSelected,
    onSelectRow,
    onSelectAllFromPage,
    showSelectAllButton,
    onSelectAll: showSelectAllButton ? handleSelectAll : undefined,
  };
}
